import React, { useState, useMemo } from 'react';
import ReactECharts from 'echarts-for-react';

const LGM_ORANGE = '#F15A29';
const LGM_PURPLE = '#662D91';

const SupplierBenchmark: React.FC = () => {
  const [selectedSupplier, setSelectedSupplier] = useState<string | null>(null);
  const [regionFilter, setRegionFilter] = useState<string>('All');

  // Demo supplier data - memoized to prevent recreation on every render
  const suppliers = useMemo(
    () => [
      {
        name: 'Mfg A',
        region: 'USA',
        price: 80,
        quality: 90,
        time: 95,
        audit: 88,
        volume: 300,
      },
      {
        name: 'Mfg B',
        region: 'India',
        price: 70,
        quality: 85,
        time: 85,
        audit: 92,
        volume: 250,
      },
      {
        name: 'Mfg C',
        region: 'China',
        price: 60,
        quality: 78,
        time: 88,
        audit: 80,
        volume: 180,
      },
      {
        name: 'Mfg D',
        region: 'EU',
        price: 75,
        quality: 82,
        time: 92,
        audit: 86,
        volume: 220,
      },
      {
        name: 'Mfg E',
        region: 'India',
        price: 68,
        quality: 88,
        time: 90,
        audit: 90,
        volume: 200,
      },
      {
        name: 'Mfg F',
        region: 'USA',
        price: 85,
        quality: 92,
        time: 97,
        audit: 91,
        volume: 320,
      },
    ],
    []
  );

  // Apply region filter
  const filteredSuppliers = useMemo(() => {
    if (regionFilter === 'All') return suppliers;
    return suppliers.filter(s => s.region === regionFilter);
  }, [regionFilter, suppliers]);

  // Default to first supplier in view
  const selected =
    filteredSuppliers.find(s => s.name === selectedSupplier) ||
    filteredSuppliers[0];

  // Scatter chart config
  const scatterOption = {
    title: {
      text: 'Supplier Benchmark: Price vs Quality',
      left: 'center',
      textStyle: { color: '#4A4A4A', fontWeight: '600' },
    },
    tooltip: {
      trigger: 'item',
      formatter: (p: {
        data: {
          name: string;
          region: string;
          value: number[];
        };
      }) =>
        `<b>${p.data.name}</b><br/>
         🌍 Region: ${p.data.region}<br/>
         💰 Price Index: ${p.data.value[0]}<br/>
         🧪 Quality Score: ${p.data.value[1]}<br/>
         ⏱️ Timeliness: ${p.data.value[2]}%<br/>
         📦 Volume: ${p.data.value[3]}`,
    },
    grid: { left: '6%', right: '6%', bottom: '10%', containLabel: true },
    xAxis: {
      name: 'Price Index',
      type: 'value',
      splitLine: { lineStyle: { type: 'dashed' } },
    },
    yAxis: {
      name: 'Quality Score',
      type: 'value',
      splitLine: { lineStyle: { type: 'dashed' } },
    },
    visualMap: {
      type: 'continuous',
      dimension: 2, // Timeliness dimension
      min: 70,
      max: 100,
      orient: 'horizontal',
      right: 20,
      top: 20,
      text: ['On-time %', ''],
      inRange: { color: ['#FEE5D9', '#F15A29', '#662D91'] },
    },
    series: [
      {
        name: 'Suppliers',
        type: 'scatter',
        symbolSize: (val: number[]) => Math.sqrt(val[3]) * 2.5,
        data: filteredSuppliers.map(s => ({
          name: s.name,
          region: s.region,
          value: [s.price, s.quality, s.time, s.volume],
        })),
        emphasis: {
          focus: 'self',
          label: { show: true, formatter: '{b}', position: 'top' },
          itemStyle: { borderColor: '#000', borderWidth: 1 },
        },
      },
    ],
  };

  // Radar chart config for selected supplier
  const radarOption = {
    title: {
      text: `Supplier Profile: ${selected.name}`,
      left: 'center',
      textStyle: { color: '#4A4A4A', fontWeight: '600' },
    },
    radar: {
      indicator: [
        { name: 'Price Index', max: 100 },
        { name: 'Quality Score', max: 100 },
        { name: 'Timeliness', max: 100 },
        { name: 'Audit Score', max: 100 },
      ],
      splitLine: { lineStyle: { color: '#ccc' } },
      splitArea: { areaStyle: { color: ['#fff', '#f9f9f9'] } },
    },
    series: [
      {
        type: 'radar',
        data: [
          {
            value: [
              selected.price,
              selected.quality,
              selected.time,
              selected.audit,
            ],
            name: selected.name,
            areaStyle: { color: LGM_ORANGE, opacity: 0.3 },
            lineStyle: { color: LGM_PURPLE, width: 2 },
          },
        ],
      },
    ],
  };

  return (
    <div style={styles.containerStyle}>
      {/* Header Row */}
      <div style={styles.headerRowStyle}>
        <h2 style={styles.headerTitleStyle}>Supplier Benchmark Dashboard</h2>
        <select
          value={regionFilter}
          onChange={e => setRegionFilter(e.target.value)}
          style={styles.selectStyle}>
          <option value="All">🌍 All Regions</option>
          <option value="USA">🇺🇸 USA</option>
          <option value="India">🇮🇳 India</option>
          <option value="China">🇨🇳 China</option>
          <option value="EU">🇪🇺 EU</option>
        </select>
      </div>

      {/* Scatter Plot */}
      <div style={styles.chartCardStyle}>
        <ReactECharts
          option={scatterOption}
          style={{ height: 400 }}
          onEvents={{
            click: (params: { data: { name: string } }) =>
              setSelectedSupplier(params.data.name),
          }}
        />
      </div>

      {/* Radar Chart */}
      <div style={styles.chartCardStyle}>
        <ReactECharts option={radarOption} style={{ height: 400 }} />
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  containerStyle: {
    padding: '24px', // p-6
    backgroundColor: '#FAFAFA',
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    gap: '24px', // space-y-6
  } as React.CSSProperties,
  headerRowStyle: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#ffffff', // bg-white
    borderRadius: '16px', // rounded-2xl
    boxShadow:
      '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)', // shadow
    padding: '16px', // p-4
    marginBottom: '8px', // mb-2
  },
  headerTitleStyle: {
    fontSize: '18px', // text-lg
    fontWeight: '600', // font-semibold
    color: '#4A4A4A',
  },
  selectStyle: {
    border: '1px solid #d1d5db', // border-gray-300
    borderRadius: '9999px', // rounded-full
    padding: '6px 16px', // px-4 py-1.5
    fontSize: '14px', // text-sm
    fontWeight: '500', // font-medium
    color: '#4A4A4A',
    backgroundColor: '#ffffff', // bg-white
    transition: 'border-color 300ms',
    outline: 'none',
    cursor: 'pointer',
  },
  chartCardStyle: {
    backgroundColor: '#ffffff', // bg-white
    borderRadius: '16px', // rounded-2xl
    boxShadow:
      '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)', // shadow
    padding: '16px', // p-4
  },
};

export default SupplierBenchmark;
